import { Button, LoaderIcon, Scrollbar, Select } from '@repo/go-ui';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';

import type { UnitsSettingsStore } from 'src/services/units-settings-service';

import styles from './units-settings.module.scss';

type Props = {
  store: UnitsSettingsStore;
};

export const UnitsSettings = wrap<Props>(function UnitsSettings({ store }) {
  const { t } = useTranslation();

  if (store.status === 'loading') {
    return (
      <div className={styles.state}>
        <LoaderIcon size="m" />
      </div>
    );
  }

  if (store.status === 'error') {
    return (
      <div className={styles.state} role="alert">
        <p className={styles.stateText}>{t('displaySettings:unitsSettings.loadError')}</p>
        <Button size="s" type="button" variant="secondary" onClick={store.retry}>
          {t('displaySettings:unitsSettings.retry')}
        </Button>
      </div>
    );
  }

  if (store.status !== 'ready') {
    return null;
  }

  return (
    <Scrollbar>
      <div className={styles.list}>
        {store.items.map(({ fieldId, label, displayValue, options, isDisabled, isSaving, hasError, errorMessage }) => {
          const translatedLabel = t(`labels:${fieldId}.label`, { defaultValue: label });
          const rowErrorMessage = errorMessage || t('displaySettings:unitsSettings.saveError');

          return (
            <div className={styles.item} key={fieldId}>
              <p className={isDisabled ? `${styles.column} ${styles.columnDisabled}` : styles.column}>
                {translatedLabel}
              </p>
              <div className={styles.control}>
                <Select<number>
                  className={styles.select}
                  disabled={isDisabled}
                  loading={isSaving}
                  options={options}
                  placeholder={t('placeholders.chooseValue')}
                  size="s"
                  status={hasError ? 'error' : undefined}
                  value={displayValue ?? undefined}
                  onChange={(value) => store.changeUnit(fieldId, value)}
                />
              </div>
              {hasError && (
                <p className={styles.error} role="alert">
                  {rowErrorMessage}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Scrollbar>
  );
});
